import type { WalletAccount, WalletWithRequiredFeatures } from '@mysten/wallet-standard';
import { createStore } from 'zustand';
import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage, persist } from 'zustand/middleware';
import { getWalletUniqueIdentifier } from '../utils/wallet.util';
import { WalletConnectionStatus } from '../types/wallet-connection-status.type';
import { WalletSliceKey, WalletSlice } from '../types/wallet-slice.type';

export type WalletActions = {
	setConnectionStatus: (walletType: WalletSliceKey, connectionStatus: WalletConnectionStatus) => void;
	setWalletConnected: (
		walletType: WalletSliceKey,
		wallet: WalletWithRequiredFeatures,
		connectedAccounts: readonly WalletAccount[],
		selectedAccount: WalletAccount | null,
	) => void;
	setWalletDisconnected: (walletType: WalletSliceKey) => void;
	setAccountSwitched: (walletType: WalletSliceKey, selectedAccount: WalletAccount) => void;
	updateWalletAccounts: (walletType: WalletSliceKey, accounts: readonly WalletAccount[]) => void;
	setWalletAuthToken: (walletType: WalletSliceKey, token: string | null | undefined) => void;
};

export type WalletStore = ReturnType<typeof createWalletStore>;

export type StoreState = {
	suiWallet: WalletSlice;
	zkLoginWallet: WalletSlice;
	primaryWalletType: WalletSliceKey | null;
} & WalletActions;

type WalletConfiguration = {
	storage: StateStorage;
	storageKey: string;
};

const createEmptyWalletSlice = (): WalletSlice => ({
	accounts: [] as WalletAccount[],
	currentWallet: null,
	currentAccount: null,
	lastConnectedAccountAddress: null,
	lastConnectedWalletName: null,
	connectionStatus: 'disconnected',
	authToken: undefined,
});

const persistableSlice = ({ lastConnectedWalletName, lastConnectedAccountAddress, authToken }: WalletSlice) => ({
	lastConnectedWalletName,
	lastConnectedAccountAddress,
	authToken,
});

export function createWalletStore({ storage, storageKey }: WalletConfiguration) {
	return createStore<StoreState>()(
		persist(
			(set, get) => ({
				suiWallet: createEmptyWalletSlice(),
				zkLoginWallet: createEmptyWalletSlice(),
				primaryWalletType: null,
				setConnectionStatus(walletType, connectionStatus) {
					set((state) => ({
						[walletType]: {
							...state[walletType],
							connectionStatus,
						},
					}));
				},
				setWalletConnected(walletType, wallet, connectedAccounts, selectedAccount) {
					set((state) => ({
						[walletType]: {
							...createEmptyWalletSlice(),
							accounts: connectedAccounts,
							currentWallet: wallet,
							currentAccount: selectedAccount,
							lastConnectedWalletName: getWalletUniqueIdentifier(wallet),
							lastConnectedAccountAddress: selectedAccount?.address ?? null,
							connectionStatus: 'connected',
							authToken: walletType === 'suiWallet' && state.zkLoginWallet.currentWallet ? null : state[walletType].authToken,
						},
						primaryWalletType: walletType === 'zkLoginWallet' ? 'zkLoginWallet' : (state.primaryWalletType ?? 'suiWallet'),
					}));
				},
				setWalletDisconnected(walletType) {
					set((state) => {
						const nextState: Partial<StoreState> = {
							[walletType]: {
								...createEmptyWalletSlice(),
								authToken: state[walletType].authToken ?? undefined,
							},
						};

						if (state.primaryWalletType === walletType) {
							const fallbackType: WalletSliceKey | null =
								walletType === 'zkLoginWallet' && state.suiWallet.currentWallet
									? 'suiWallet'
									: walletType === 'suiWallet' && state.zkLoginWallet.currentWallet
										? 'zkLoginWallet'
										: null;

							nextState.primaryWalletType = fallbackType;
						}

						return nextState;
					});
				},
				setAccountSwitched(walletType, selectedAccount) {
					set((state) => ({
						[walletType]: {
							...state[walletType],
							currentAccount: selectedAccount,
							lastConnectedAccountAddress: selectedAccount.address,
						},
					}));
				},
				updateWalletAccounts(walletType, accounts) {
					const currentAccount = get()[walletType].currentAccount;

					set((state) => ({
						[walletType]: {
							...state[walletType],
							accounts,
							currentAccount: (currentAccount && accounts.find(({ address }) => address === currentAccount.address)) || accounts[0] || null,
						},
					}));
				},
				setWalletAuthToken(walletType, token) {
					set((state) => ({
						[walletType]: {
							...state[walletType],
							authToken: token,
						},
					}));
				},
			}),
			{
				name: storageKey,
				storage: createJSONStorage(() => storage),
				partialize: ({ suiWallet, zkLoginWallet }) => ({
					suiWallet: persistableSlice(suiWallet),
					zkLoginWallet: persistableSlice(zkLoginWallet),
				}),
			},
		),
	);
}
